import { useState } from 'react';
import { Shield } from 'lucide-react';
import { useAdminOverview } from '../../hooks/use-admin-overview';
import { AdminActivitySection } from './admin-activity-section';
import { AdminCasesSection } from './admin-cases-section';
import { AdminTabs, type AdminTab } from './admin-tabs';
import { AdminUsersList } from './admin-users-list';

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-surface px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</p>
      <p className="mt-1 text-xl font-semibold text-text-primary">{value}</p>
    </div>
  );
}

export function AdminPage() {
  const { data, isLoading, isError, error } = useAdminOverview();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const totalSources = data?.cases.reduce((sum, item) => sum + item.sources.length, 0) ?? 0;
  const totalEvents = data?.cases.reduce((sum, item) => sum + item.totalEvents, 0) ?? 0;

  return (
    <div className="w-full overflow-y-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-accent">
            <Shield size={16} />
            <span className="text-xs font-medium">Admin</span>
          </div>
          <h1 className="mt-1 text-lg font-semibold text-text-primary">Systemoversikt</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            Alle saker, brukere, aktiviteter og opplastede kilder.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-tertiary">Laster adminoversikt…</p>}

      {isError && (
        <p className="rounded-md bg-danger/8 px-3 py-2 text-xs text-danger/80">
          Kunne ikke laste adminoversikt: {error.message}
        </p>
      )}

      {data && (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-4">
            <StatCard label="Saker" value={data.cases.length} />
            <StatCard label="Brukere" value={data.users.length} />
            <StatCard label="Kilder" value={totalSources} />
            <StatCard label="Hendelser" value={totalEvents} />
          </div>

          <AdminTabs
            activeTab={activeTab}
            counts={{
              users: data.users.length,
              cases: data.cases.length,
              events: data.activityCount,
            }}
            onTabChange={setActiveTab}
          />

          {activeTab === 'users' && <AdminUsersList users={data.users} />}
          {activeTab === 'cases' && <AdminCasesSection cases={data.cases} />}
          {activeTab === 'events' && <AdminActivitySection />}
        </div>
      )}
    </div>
  );
}
